import {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { IconChevronUp } from '@tabler/icons-react';
import { Button } from '../../primitives/Button.js';
import { Popover } from '../../primitives/Popover.js';
import { Pressable } from '../../primitives/Pressable.js';
import { WP_ICON_NAV } from '../../icons/constants.js';
import { cx } from '../../utils/cx.js';
import styles from './DataTableSelectionBar.module.css';
import menuStyles from './DataTableMenus.module.css';

export type DataTableSelectionDockGroupItem = {
  id: string;
  label: string;
  /** Longer hint under the label in the menu. */
  hint?: string;
  icon?: ReactNode;
  disabled?: boolean;
  danger?: boolean;
  onSelect: () => void;
};

export type DataTableSelectionDockGroupProps = {
  /** Group trigger label in the dock. */
  label: string;
  icon?: ReactNode;
  /** Longer description for aria-label on the trigger; defaults to `label`. */
  hint?: string;
  items: readonly DataTableSelectionDockGroupItem[];
  disabled?: boolean;
  primary?: boolean;
  /** Accessible name for the menu panel. */
  menuLabel?: string;
};

type ExclusiveApi = {
  activeId: string | null;
  openGroup: (id: string) => void;
  closeGroup: (id: string) => void;
};

const ExclusiveCtx = createContext<ExclusiveApi | null>(null);

/**
 * One open dock group at a time — avoids stacked menus and sibling chrome flicker.
 * Wrap the dock `actions` children (optional; groups work standalone without it).
 */
export function DataTableSelectionDockGroupExclusive({ children }: { children: ReactNode }) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const value = useMemo<ExclusiveApi>(
    () => ({
      activeId,
      openGroup: (id) => setActiveId(id),
      closeGroup: (id) => setActiveId((cur) => (cur === id ? null : cur)),
    }),
    [activeId],
  );
  return <ExclusiveCtx.Provider value={value}>{children}</ExclusiveCtx.Provider>;
}

/** Share exclusive open-id with custom dock chips (e.g. Partager wizard). */
export function useDataTableSelectionDockExclusive(): ExclusiveApi | null {
  return useContext(ExclusiveCtx);
}

/**
 * Dock selection group: one chip; toggle opens a drop-up of options.
 * Motion = state (open/closed + fluid item hover), not press-scale thrash on the bar.
 */
export function DataTableSelectionDockGroup({
  label,
  icon,
  hint,
  items,
  disabled,
  primary,
  menuLabel,
}: DataTableSelectionDockGroupProps) {
  const reactId = useId();
  const exclusive = useContext(ExclusiveCtx);
  const [localOpen, setLocalOpen] = useState(false);

  const open = exclusive ? exclusive.activeId === reactId : localOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (exclusive) {
        if (next) exclusive.openGroup(reactId);
        else exclusive.closeGroup(reactId);
        return;
      }
      setLocalOpen(next);
    },
    [exclusive, reactId],
  );

  const panelId = useId();
  const tip = hint ?? label;
  const allDisabled = disabled || items.every((it) => it.disabled);

  return (
    <Popover
      opened={open}
      onChange={setOpen}
      portal
      align="start"
      portalMinWidth={200}
      portalMaxWidth={280}
      portalEstimatedHeight={Math.min(360, 56 + items.length * 48)}
      portalGap={10}
      portalZIndex="calc(var(--wp-z-popover-over-modal, 10000) + 20)"
      toggleOnTargetClick={false}
      target={
        <Button
          type="button"
          size="xs"
          variant={primary ? 'primary' : 'secondaryPill'}
          disabled={allDisabled}
          pressFeedback={false}
          title={tip !== label ? tip : undefined}
          aria-label={tip}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          className={cx(
            styles.dockAction,
            styles.dockGroupTrigger,
            primary ? styles.dockActionPrimary : styles.dockActionSecondary,
            open && styles.dockGroupTriggerOpen,
          )}
          data-datatable-rowclick-ignore
          onClick={() => {
            if (allDisabled) return;
            setOpen(!open);
          }}
        >
          {icon ? <span className={styles.dockActionIcon}>{icon}</span> : null}
          <span className={styles.dockActionLabel}>{label}</span>
          <IconChevronUp
            {...WP_ICON_NAV}
            size={14}
            stroke={1.75}
            className={cx(styles.dockGroupChev, open && styles.dockGroupChevOpen)}
            aria-hidden
          />
        </Button>
      }
    >
      <div
        id={panelId}
        className={cx(menuStyles.rowMenu, styles.dockGroupMenu)}
        role="menu"
        aria-label={menuLabel ?? label}
        data-datatable-rowclick-ignore
      >
        {items.map((it) => (
          <Pressable
            key={it.id}
            type="button"
            role="menuitem"
            skipPressFeedback
            disabled={it.disabled || disabled}
            className={cx(
              menuStyles.rowMenuItem,
              styles.dockGroupItem,
              it.danger && menuStyles.rowMenuItemDanger,
            )}
            onClick={() => {
              if (it.disabled || disabled) return;
              setOpen(false);
              it.onSelect();
            }}
          >
            {it.icon ? <span className={styles.dockGroupItemIcon}>{it.icon}</span> : null}
            <span className={styles.dockGroupItemText}>
              <span className={styles.dockGroupItemLabel}>{it.label}</span>
              {it.hint ? <span className={styles.dockGroupItemHint}>{it.hint}</span> : null}
            </span>
          </Pressable>
        ))}
      </div>
    </Popover>
  );
}
